import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ShieldCheck } from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Upgrade() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState("");
  const { user, refresh, isPaid } = useAuth();

  const reconcile = useCallback(async (orderId) => {
    try {
      const { data } = await api.get(`/payments/status/${orderId}`);
      if (data.status === "paid") {
        await refresh();
        setLastError("");
        sessionStorage.removeItem("neetone_pending_order");
        toast.success("MasterPlan activated");
      } else if (data.status === "failed" || data.status === "under_review") {
        sessionStorage.removeItem("neetone_pending_order");
        setLastError(data.reason);
        toast.error(data.reason);
      } else {
        toast.message("Payment not completed");
      }
    } catch (e) {
      const msg = errMsg(e);
      setLastError(msg);
      toast.error(msg);
    }
  }, [refresh]);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const reconciledRef = useRef(false);
  useEffect(() => {
    const pending = sessionStorage.getItem("neetone_pending_order");
    if (pending && !isPaid && !reconciledRef.current) {
      reconciledRef.current = true;
      reconcile(pending);
    }
  }, [isPaid, reconcile]);

  const pay = async () => {
    setBusy(true);
    setLastError("");
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay checkout");
      const { data } = await api.post("/payments/order");
      sessionStorage.setItem("neetone_pending_order", data.order_id);
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "NeetOne",
        description: "MasterPlan — full access",
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        theme: { color: "#1A365D" },
        handler: async (r) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
            });
            await refresh();
            sessionStorage.removeItem("neetone_pending_order");
            toast.success("MasterPlan activated");
          } catch (e) {
            // The payment went through at Razorpay; fall back to server-side
            // reconciliation instead of showing the student an error.
            await reconcile(r.razorpay_order_id);
          }
        },
        modal: { ondismiss: () => reconcile(data.order_id) },
      });
      rzp.on("payment.failed", (resp) => {
        const err = resp?.error || {};
        setLastError(err.description || "The payment could not be completed.");
        toast.error(err.description || "Payment failed");
      });
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="upgrade-page">
      <PageHead title="Plans" subtitle="Unlock every row of Karnataka KEA 2025 data for the season." />

      {isPaid && (
        <div
          data-testid="already-paid-banner"
          className="flex items-center gap-2 border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
        >
          <ShieldCheck className="h-4 w-4" /> You already have full MasterPlan access.
        </div>
      )}

      <div className="grid gap-px border border-slate-200 bg-slate-200 lg:grid-cols-2">
        {plans.map((p) => (
          <div
            key={p.id}
            data-testid={`upgrade-plan-${p.id}`}
            className={p.id === "master" ? "bg-[#1A365D] p-8 text-white" : "bg-white p-8"}
          >
            <h3 className="font-display text-xl font-bold">{p.name}</h3>
            {p.list_price > p.amount ? (
              <>
                <div className="mt-2 flex items-baseline gap-2.5">
                  <span className="font-mono-num text-4xl font-semibold">₹{p.amount.toLocaleString("en-IN")}</span>
                  <span className="font-mono-num text-lg text-blue-200/80 line-through">
                    ₹{p.list_price.toLocaleString("en-IN")}
                  </span>
                </div>
                <span
                  data-testid="upgrade-discount-badge"
                  className="mt-2 inline-block bg-amber-400 px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest text-[#1A365D]"
                >
                  Save {p.discount_pct}% · launch offer
                </span>
              </>
            ) : (
              <p className="mt-2 font-mono-num text-4xl font-semibold">₹{p.amount.toLocaleString("en-IN")}</p>
            )}
            <ul className={`mt-6 space-y-2.5 text-sm ${p.id === "master" ? "text-blue-50" : "text-slate-700"}`}>
              {p.features.map((f) => (
                <li key={f} className="flex gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 opacity-70" /> {f}
                </li>
              ))}
            </ul>
            {p.id === "master" ? (
              <button
                data-testid="razorpay-pay-btn"
                disabled={busy || isPaid}
                onClick={pay}
                className="mt-8 w-full bg-white px-4 py-3 text-sm font-semibold text-[#1A365D] hover:bg-blue-50 disabled:opacity-40"
              >
                {isPaid ? "Active" : busy ? "Opening checkout…" : `Pay ₹${p.amount.toLocaleString("en-IN")} with Razorpay`}
              </button>
            ) : (
              <p className="mt-8 border border-slate-300 px-4 py-3 text-center text-sm font-semibold text-slate-500">
                {isPaid ? "Superseded by MasterPlan" : "Your current plan"}
              </p>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-500">
        Payments are processed by Razorpay. NeetOne never stores your card details.
      </p>

      {lastError && (
        <div
          data-testid="payment-error-banner"
          className="border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
        >
          <span className="font-semibold">Payment not completed.</span> {lastError}
        </div>
      )}
    </div>
  );
}
